import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The row above a product grid: the orders the listing can be read in, and how
 * much of it there is. Flat named parts a tenant arranges in its own JSX, each
 * painting itself.
 *
 *   <SortStrip className="mb-6">
 *     <SortStripOptions>
 *       {options.map((option) => (
 *         <SortStripOption key={option.value}>
 *           <SortStripOptionButton
 *             aria-pressed={option.value === selected}
 *             onClick={() => { select(option.value); }}
 *           >
 *             {option.label}
 *           </SortStripOptionButton>
 *         </SortStripOption>
 *       ))}
 *     </SortStripOptions>
 *
 *     <SortStripTotal>{total}</SortStripTotal>
 *   </SortStrip>
 *
 * Which order is the current one is carried by `aria-pressed` and by nothing
 * else. It is already the attribute that says a toggle is on, it is what a
 * screen reader announces, and it rides the native prop spread — so the parts
 * take no `isCurrent` prop, and the current option's colour and underline are
 * selected from the DOM the same way `@vs-shop/ui`'s controls select theirs
 * from `data-[checked]`. One statement of the state, in the place that has to
 * carry it regardless.
 *
 * No `variant`: a sort strip paints the same above every grid. What differs
 * between listings is which orders are offered and whether a total is composed
 * at all — both of them the composition's.
 *
 * No strings: the option labels and the total are children, because the orders
 * a listing offers and the way it counts its products are a tenant's domain and
 * its language.
 *
 * Server-first. Nothing here holds state; a tenant's own client component
 * supplies the handlers, so the strip itself costs no client JavaScript.
 */

/**
 * The row itself, ruled off from the grid below it.
 *
 * It scrolls sideways rather than wrapping: the orders are a single line of
 * short labels, and a strip that reflowed to two lines on a phone would push
 * the first product below the fold. The vertical clip is what keeps that scroll
 * from also exposing the current option's underline, which hangs below its
 * label.
 *
 * `items-center` and nothing else: the row does not distribute its children,
 * because a strip composed with no total would then place the orders wherever
 * two-child spacing happened to leave them. Each part anchors itself instead.
 */
const sortStripClasses =
  'scrollbar-hidden border-foreground/10 lap-lg:gap-16 flex items-center gap-8 overflow-x-auto overflow-y-hidden border-b pb-4';

export type SortStripProps = ComponentProps<'div'>;

/** The sort row above a product grid. */
export const SortStrip = ({ className, ...props }: SortStripProps) => (
  <div data-slot="sort-strip" className={clsxm(sortStripClasses, className)} {...props} />
);

/**
 * The orders, read as one line. The gap opens up on a wide viewport, where the
 * row has room the labels do not need.
 */
const sortStripOptionsClasses = 'lap-lg:gap-16 flex gap-8';

export type SortStripOptionsProps = ComponentProps<'ul'>;

/** The list of orders. */
export const SortStripOptions = ({ className, ...props }: SortStripOptionsProps) => (
  <ul
    data-slot="sort-strip-options"
    className={clsxm(sortStripOptionsClasses, className)}
    {...props}
  />
);

export type SortStripOptionProps = ComponentProps<'li'>;

/**
 * One order's cell in the list.
 *
 * Carries no recipe of its own: the row's spacing belongs to the list and the
 * type to the control inside, so this cell has nothing left to paint. It is a
 * part regardless — a list of orders has to be a list of items to read as one,
 * and leaving the `<li>` to tenant JSX would leave the one element in the
 * anatomy with no slot on it.
 */
export const SortStripOption = ({ className, ...props }: SortStripOptionProps) => (
  <li data-slot="sort-strip-option" className={optionalClassName(clsxm(className))} {...props} />
);

/**
 * One order, as the control that switches to it.
 *
 * The label cannot wrap — an order broken across two lines would double the
 * row's height for every other order beside it — which is why the row scrolls
 * instead. `group` is what the underline below reads the pressed state through.
 */
const sortStripOptionButtonClasses = [
  'font-heading',
  'group lap:text-sm/5 relative text-xs/[18px] tracking-wider whitespace-nowrap uppercase',
  'text-foreground-muted aria-pressed:text-foreground',
];

/**
 * The mark under the order the listing is currently read in.
 *
 * Always rendered and hidden until its control is pressed, rather than composed
 * only under the current one: the state lives on the button as `aria-pressed`,
 * so the mark that follows it is a rule rather than a branch. It hangs below the
 * label — outside the button's own box — so it lands on the row's rule instead
 * of under the text.
 */
const sortStripOptionUnderlineClasses =
  'bg-primary lap:rounded-t-none absolute -bottom-5 left-0 hidden h-1 w-full rounded-t-full group-aria-pressed:block';

export type SortStripOptionButtonProps = ComponentProps<'button'>;

/**
 * One order's control. Which order is current arrives as `aria-pressed`, and
 * what the order is called arrives as children.
 */
export const SortStripOptionButton = ({
  className,
  children,
  ...props
}: SortStripOptionButtonProps) => (
  <button
    type="button"
    data-slot="sort-strip-option-button"
    className={clsxm(sortStripOptionButtonClasses, className)}
    {...props}
  >
    {children}
    <span
      data-slot="sort-strip-option-underline"
      className={sortStripOptionUnderlineClasses}
    ></span>
  </button>
);

/**
 * How much of the listing there is.
 *
 * `ml-auto` is the total's own place at the trailing end of the row, not
 * spacing the row hands it: a strip composed with only a total still reads it
 * where a total belongs.
 */
const sortStripTotalClasses =
  'text-foreground-muted lap:text-[15px] ml-auto text-xs/[18px] whitespace-nowrap';

export type SortStripTotalProps = ComponentProps<'span'>;

/** The product count at the trailing end of the row. */
export const SortStripTotal = ({ className, ...props }: SortStripTotalProps) => (
  <span
    data-slot="sort-strip-total"
    className={clsxm(sortStripTotalClasses, className)}
    {...props}
  />
);
